import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Sync as RefreshIcon,
  FiberManualRecord as LiveDot,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { appColors } from '../Config/Theme';
import { IncidentRosterPerson } from '../Types/Incident';
import { getIncidentRoster, getIncidentSummary, getIncidentDetail } from '../Services/IncidentService';
import RosterGroupCard from '../Components/Headcount/RosterGroupCard';
import WorkingTogetherBanner from '../Components/Headcount/WorkingTogetherBanner';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import IncidentInfoBar from '../Components/Incidents/IncidentInfoBar';
import ClosedIncidentBanner from '../Components/Incidents/ClosedIncidentBanner';
import { formatTime } from '../Utils/dateHelpers';
import { isIncidentClosed, canEditClosedRoster } from '../Utils/incidentHelpers';
import { useSecurity } from '../Contexts/SecurityProvider';
import { useIncidentAssignments } from '../Hooks/useIncidentAssignments';
import {
  buildRosterViewGroups,
  getHeadcountCounts,
  getAlphaRangeCounts,
  buildHeadcountNavQuery,
  personMatchesRosterSearch,
  getFullRosterState,
  getFullRosterDescription,
  getFullRosterCtaLabel,
  getFullRosterSectionLabel,
  getFullRosterCountSummary,
  getRosterStateLine,
  RosterViewGroup,
} from '../Utils/rosterViewHelpers';
import { useIncidentRealtime } from '../Hooks/useIncidentRealtime';
import { applyRosterPersonStatusChanged } from '../realtime/incidentRealtimeHelpers';
import type {
  RosterPersonStatusChangedEvent,
  IncidentClosedEvent,
  IncidentRefreshRequestedEvent,
} from '../realtime/incidentRealtimeTypes';
import { isValidHeadcountStatus } from '../realtime/incidentRealtimeHelpers';
import {
  isPersonDefaultVisible,
  computeRosterScopeCounts,
} from '../features/incidents/accountabilityScope';

type CategoryTab = 'all' | 'location' | 'department' | 'personType' | 'rosterContext';

const CATEGORY_TABS: { value: CategoryTab; label: string }[] = [
  { value: 'all', label: 'All Groups' },
  { value: 'location', label: 'Location' },
  { value: 'department', label: 'Department' },
  { value: 'personType', label: 'Person Type' },
  { value: 'rosterContext', label: 'Roster Context' },
];

const AUTO_REFRESH_MS = 30_000;
const AUTO_REFRESH_REALTIME_MS = 120_000;
const MAX_SEARCH_RESULTS = 8;

const RosterViewSelectionPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const { hasAnyRole, security } = useSecurity();

  const [rosterPeople, setRosterPeople] = useState<IncidentRosterPerson[]>([]);
  const [incidentName, setIncidentName] = useState('');
  const [startedAt, setStartedAt] = useState('');
  const [incidentLocation, setIncidentLocation] = useState('');
  const [incidentType, setIncidentType] = useState<string | null | undefined>(undefined);
  const [incidentStatus, setIncidentStatus] = useState<string | undefined>(undefined);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [categoryTab, setCategoryTab] = useState<CategoryTab>('all');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [realtimeSnackbar, setRealtimeSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const loadData = useCallback(async (showSpinner = true) => {
    if (!incidentId) return;
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const [rosterResult, summaryResult, detailResult] = await Promise.all([
        getIncidentRoster(incidentId),
        getIncidentSummary(incidentId).catch(() => null),
        getIncidentDetail(incidentId).catch(() => null),
      ]);

      setRosterPeople(rosterResult.people);

      const sum = summaryResult?.summary;
      setIncidentName(sum?.incidentName || detailResult?.incidentName || '');
      setStartedAt(sum?.startedAtUtc || detailResult?.startedAtUtc || '');
      setIncidentLocation(sum?.location || detailResult?.location || '');
      setIncidentType(sum?.incidentType !== undefined ? sum.incidentType : detailResult?.incidentType);
      setIncidentStatus(sum?.status ?? detailResult?.status);

      const serverTime = summaryResult?.serverTimeUtc ?? rosterResult.serverTimeUtc;
      if (serverTime) {
        setLastUpdated(formatTime(serverTime));
      } else {
        setLastUpdated(formatTime(new Date().toISOString()));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => { loadData(); }, [loadData]);

  const isClosed = isIncidentClosed({ status: incidentStatus });
  const { assignments } = useIncidentAssignments(isClosed ? incidentId : undefined);
  const closedRosterEditable = isClosed && canEditClosedRoster({
    hasAnyRole,
    currentUserId: security?.appUserId,
    assignments,
  });

  // ── Realtime SignalR ──
  const handleRealtimeStatusChanged = useCallback((event: RosterPersonStatusChangedEvent) => {
    if (!isValidHeadcountStatus(event.effectiveHeadcountStatus)) return;
    setRosterPeople((prev) => applyRosterPersonStatusChanged(prev, event));
    setLastUpdated(formatTime(event.lastUpdatedAtUtc));
  }, []);

  const handleRealtimeIncidentClosed = useCallback((event: IncidentClosedEvent) => {
    setIncidentStatus('closed');
    setRealtimeSnackbar({
      open: true,
      message: event.closedByDisplayName
        ? `Incident closed by ${event.closedByDisplayName}`
        : 'Incident has been closed',
    });
  }, []);

  const handleRealtimeRefreshRequested = useCallback((event: IncidentRefreshRequestedEvent) => {
    loadData(false);
    if (event.reason === 'manualAdd') {
      setRealtimeSnackbar({ open: true, message: 'New person added. Roster refreshed.' });
    }
  }, [loadData]);

  const handleRealtimeReconnect = useCallback(() => {
    loadData(false);
  }, [loadData]);

  const { connectionState: realtimeState } = useIncidentRealtime({
    incidentId,
    enabled: !loading && !isClosed,
    onRosterPersonStatusChanged: handleRealtimeStatusChanged,
    onIncidentClosed: handleRealtimeIncidentClosed,
    onIncidentRefreshRequested: handleRealtimeRefreshRequested,
    onReconnect: handleRealtimeReconnect,
  });

  const realtimeConnected = realtimeState === 'connected';

  // ── Auto-refresh polling (slower when realtime is active) ──
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!isClosed) {
      const interval = realtimeConnected ? AUTO_REFRESH_REALTIME_MS : AUTO_REFRESH_MS;
      timerRef.current = setInterval(() => loadData(false), interval);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [incidentStatus, loadData, realtimeConnected, isClosed]);

  // ── Derived data ──
  const scopeCounts = useMemo(() => computeRosterScopeCounts(rosterPeople), [rosterPeople]);
  const defaultScopePeople = useMemo(() => rosterPeople.filter(isPersonDefaultVisible), [rosterPeople]);
  const fullRosterCounts = useMemo(() => getHeadcountCounts(rosterPeople), [rosterPeople]);
  const groups = useMemo(() => buildRosterViewGroups(defaultScopePeople), [defaultScopePeople]);
  const alphaRanges = useMemo(() => getAlphaRangeCounts(defaultScopePeople), [defaultScopePeople]);
  const rosterState = useMemo(() => isClosed ? 'complete' as const : getFullRosterState(fullRosterCounts), [isClosed, fullRosterCounts]);
  const fullRosterCtaLabel = isClosed ? 'View Full Roster' : getFullRosterCtaLabel(rosterState);
  const fullRosterDescription = isClosed ? 'View the complete roster.' : getFullRosterDescription(rosterState);
  const fullRosterSectionLabel = isClosed ? 'Full Roster' : getFullRosterSectionLabel(rosterState);
  const fullRosterCountText = getFullRosterCountSummary(fullRosterCounts, rosterState);

  // ── Search results ──
  const searchResults = useMemo(() => {
    const q = searchText.trim();
    if (!q || q.length < 2) return [];
    return rosterPeople.filter((p) => personMatchesRosterSearch(p, q)).slice(0, MAX_SEARCH_RESULTS);
  }, [rosterPeople, searchText]);

  // ── Visible groups based on category tab ──
  const visibleSections = useMemo(() => {
    const sections: { title: string; groups: RosterViewGroup[] }[] = [];
    const addSection = (title: string, grps: RosterViewGroup[]) => {
      if (grps.length > 0) sections.push({ title, groups: grps });
    };
    if (categoryTab === 'all' || categoryTab === 'location') {
      addSection('Location', groups.floors);
    }
    if (categoryTab === 'all' || categoryTab === 'department') {
      addSection('Department / Practice', groups.practices);
    }
    if (categoryTab === 'all' || categoryTab === 'personType') {
      addSection('Person Type', groups.personTypes);
    }
    if (categoryTab === 'all' || categoryTab === 'rosterContext') {
      addSection('Roster Context', groups.rosterContexts);
    }
    return sections;
  }, [categoryTab, groups]);

  // ── Total groups count for tab visibility ──
  const totalGroupCards = groups.floors.length + groups.practices.length + groups.personTypes.length + groups.rosterContexts.length;

  // ── Category counts for chips ──
  const categoryCounts: Record<CategoryTab, number> = useMemo(() => ({
    all: totalGroupCards,
    location: groups.floors.length,
    department: groups.practices.length,
    personType: groups.personTypes.length,
    rosterContext: groups.rosterContexts.length,
  }), [totalGroupCards, groups]);

  // ── Navigation ──
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchText.trim()) {
      navigate(`/incidents/${incidentId}/headcount?search=${encodeURIComponent(searchText.trim())}`);
    }
  };

  const handleSearchResultClick = (person: IncidentRosterPerson) => {
    navigate(`/incidents/${incidentId}/headcount?search=${encodeURIComponent(person.displayName)}`);
  };

  const handleGroupCardClick = (group: RosterViewGroup) => {
    const query = buildHeadcountNavQuery(group.groupType, group.rawValue);
    navigate(`/incidents/${incidentId}/headcount?${query}`);
  };

  // ── Loading ──
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Alert
          severity="error"
          action={<Button size="small" onClick={() => loadData()}>Retry</Button>}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Breadcrumbs ── */}
      <AppBreadcrumbs items={[
        { label: 'Incidents', path: '/' },
        { label: incidentName || 'Incident Dashboard', path: `/incidents/${incidentId}/incidentdashboard` },
        { label: 'Roster Views' },
      ]} />

      {/* ── Closed banner ── */}
      {isClosed && !closedRosterEditable && <ClosedIncidentBanner />}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading }}>
          Roster Views
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {!isClosed && (
            <Tooltip title="Auto-refreshing every 30s" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LiveDot sx={{
                  fontSize: 10,
                  color: appColors.status.success,
                  '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.4 }, '100%': { opacity: 1 } },
                  animation: 'pulse 2s ease-in-out infinite',
                }} />
                <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 600 }}>Live</Typography>
              </Box>
            </Tooltip>
          )}
          <Typography variant="caption" sx={{ color: appColors.text.caption }}>
            Last updated: {lastUpdated || '\u2014'}
          </Typography>
          <Button variant="text" size="small" onClick={() => loadData()} sx={{ minWidth: 32, p: 0.5, color: appColors.text.body }}>
            <RefreshIcon sx={{ fontSize: 20 }} />
          </Button>
        </Box>
      </Box>

      {/* ── Incident Info Bar ── */}
      {incidentName && (
        <IncidentInfoBar
          incidentName={incidentName}
          incidentType={incidentType}
          location={incidentLocation}
          startedAtUtc={startedAt}
          status={incidentStatus}
        />
      )}

      {/* ── Intro + Find a Person row ── */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start', justifyContent: 'space-between', gap: 3, mb: 2 }}>
        {/* Left: intro text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 0.5 }}>
            {isClosed ? 'Roster Views' : 'Choose how to begin headcount'}
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body, lineHeight: 1.6 }}>
            {isClosed
              ? 'Review final roster breakdown by group or open the full roster.'
              : 'Search for a person, start with a last-name group, open the full roster, or choose another roster view.'}
          </Typography>
          {!isClosed && scopeCounts.accountabilityCounts.total > 0 && (
            <Typography variant="body2" sx={{ color: appColors.primary.main, mt: 0.5, display: 'block', fontWeight: 700 }}>
              {getRosterStateLine(scopeCounts.accountabilityCounts)}
            </Typography>
          )}
          {!isClosed && fullRosterCounts.total > scopeCounts.accountabilityCounts.total && (
            <Typography variant="caption" sx={{ color: appColors.text.caption, mt: 0.5, display: 'block' }}>
              Full Roster: {fullRosterCounts.total} people
            </Typography>
          )}
          {!isClosed && (scopeCounts.assignedOfficeNoBadgeTodayCount ?? 0) > 0 && (
            <Typography variant="caption" sx={{ color: appColors.text.caption, mt: 0.5, display: 'block' }}>
              Showing Accountability Roster by default. Assigned-office employees without badge activity are available under Roster Context.
            </Typography>
          )}
        </Box>

        {/* Right: Find a Person */}
        <Box sx={{ width: { xs: '100%', md: 400 }, maxWidth: { md: 400 }, flexShrink: 0, position: 'relative' }}>
          <Typography
            variant="overline"
            component="label"
            htmlFor="roster-search-input"
            sx={{ color: appColors.text.caption, fontWeight: 700, letterSpacing: 1.2, display: 'block', mb: 1 }}
          >
            Find a person
          </Typography>
          <TextField
            id="roster-search-input"
            placeholder="Search by name, phone, department..."
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            aria-label="Search roster by name, phone, or department"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: '100%' }}
          />
          {/* Search results dropdown */}
          {searchResults.length > 0 && (
            <Box
              sx={{
                width: '100%',
                bgcolor: appColors.surface.card,
                border: `1px solid ${appColors.border.default}`,
                borderRadius: '0 0 12px 12px',
                boxShadow: appColors.shadow.cardHover,
                overflow: 'hidden',
                position: 'absolute',
                zIndex: 10,
              }}
            >
              <List dense disablePadding>
                {searchResults.map((p) => (
                  <ListItemButton
                    key={p.personId}
                    onClick={() => handleSearchResultClick(p)}
                    sx={{ minHeight: 44 }}
                  >
                    <ListItemText
                      primary={p.displayName}
                      secondary={[p.practice, p.floor ? `Floor ${p.floor}` : null, p.companyName].filter(Boolean).join(' \u00B7 ') || p.email || undefined}
                      slotProps={{
                        primary: { sx: { fontWeight: 600, color: appColors.text.heading, fontSize: '0.875rem' } },
                        secondary: { sx: { color: appColors.text.caption, fontSize: '0.75rem' } },
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
              {searchText.trim().length >= 2 && (
                <ButtonBase
                  onClick={() => navigate(`/incidents/${incidentId}/headcount?search=${encodeURIComponent(searchText.trim())}`)}
                  sx={{
                    width: '100%',
                    py: 1,
                    justifyContent: 'center',
                    color: appColors.primary.main,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderTop: `1px solid ${appColors.border.default}`,
                    '&:hover': { bgcolor: appColors.surface.inactive },
                  }}
                >
                  Search all in headcount
                </ButtonBase>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Not Accounted banner ── */}
      {fullRosterCounts.notAccounted > 0 && (
        <Alert
          severity="error"
          icon={<WarningIcon />}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => navigate(`/incidents/${incidentId}/headcount?status=notAccounted`)}
              sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
              aria-label={`Review ${fullRosterCounts.notAccounted} ${fullRosterCounts.notAccounted === 1 ? 'person' : 'people'} marked Not Accounted`}
            >
              Review Not Accounted
            </Button>
          }
          sx={{ mb: 2, borderRadius: '12px', '& .MuiAlert-message': { fontWeight: 600 } }}
          role="alert"
        >
          Not accounted — {fullRosterCounts.notAccounted} {fullRosterCounts.notAccounted === 1 ? 'person' : 'people'} not accounted for
        </Alert>
      )}

      {/* ── Section: Start by last name ── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" sx={{ color: appColors.text.caption, fontWeight: 700, letterSpacing: 1.2, display: 'block', mb: 0.5 }}>
          Start by last name
        </Typography>
        <Typography variant="body2" sx={{ color: appColors.text.body, mb: 1 }}>
          Choose a last-name range to begin checking people.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 1.5,
          }}
        >
          {alphaRanges.map((item) => (
            <ButtonBase
              key={item.query}
              onClick={() => navigate(`/incidents/${incidentId}/headcount?alpha=${encodeURIComponent(item.query)}`)}
              aria-label={`${item.action} with last names ${item.label.replace('\u2013', ' through ')}, ${item.count} ${item.count === 1 ? 'person' : 'people'}`}
              sx={{
                minHeight: 88,
                px: 2.5,
                py: 1.5,
                bgcolor: 'rgba(46, 139, 132, 0.06)',
                border: `2px solid ${appColors.primary.main}`,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25,
                transition: 'background-color 0.15s, box-shadow 0.15s, transform 0.1s',
                '&:hover': {
                  bgcolor: 'rgba(46, 139, 132, 0.12)',
                  boxShadow: appColors.shadow.cardHover,
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  bgcolor: 'rgba(46, 139, 132, 0.18)',
                  transform: 'translateY(0)',
                },
                '&:focus-visible': { outline: `2px solid ${appColors.primary.main}`, outlineOffset: 2 },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: appColors.text.heading, lineHeight: 1.2 }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ color: appColors.primary.main, fontWeight: 700, fontSize: '0.8rem' }}>
                {item.count} {item.count === 1 ? 'person' : 'people'}
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.primary.main, fontWeight: 700, fontSize: '0.7rem', mt: 0.25 }}>
                {item.action} →
              </Typography>
            </ButtonBase>
          ))}
        </Box>
      </Box>

      {/* ── Section: Full Roster ── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" sx={{ color: appColors.text.caption, fontWeight: 700, letterSpacing: 1.2, display: 'block', mb: 0.5 }}>
          {fullRosterSectionLabel}
        </Typography>
        <Box
          sx={{
            bgcolor: appColors.surface.card,
            border: rosterState === 'notStarted'
              ? `2px solid ${appColors.primary.main}`
              : `1px solid ${appColors.border.default}`,
            borderRadius: '12px',
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 1.5 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1.5,
            minHeight: 88,
            transition: 'box-shadow 0.15s',
            '&:hover': { boxShadow: appColors.shadow.cardHover },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: appColors.text.heading, lineHeight: 1.3 }}>
              Full Roster
            </Typography>
            <Typography variant="body2" sx={{ color: appColors.text.body, fontSize: '0.8rem', mt: 0.25 }}>
              {fullRosterDescription}
            </Typography>
            <Typography variant="body2" sx={{ color: appColors.primary.main, fontWeight: 700, fontSize: '0.8rem' }}>
              {fullRosterCountText}
            </Typography>
          </Box>
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: { xs: 'stretch', sm: 'center' } }}>
            <Button
              variant={rosterState === 'notStarted' ? 'contained' : 'outlined'}
              onClick={() => navigate(`/incidents/${incidentId}/headcount`)}
              aria-label={fullRosterCtaLabel}
              disabled={rosterState === 'emptyRoster'}
              sx={{
                fontWeight: 700,
                whiteSpace: 'nowrap',
                minHeight: 44,
                px: 3,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {fullRosterCtaLabel}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Section: Start by group ── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" sx={{ color: appColors.text.caption, fontWeight: 700, letterSpacing: 1.2, display: 'block', mb: 1 }}>
          Start by group
        </Typography>

        {/* Category tabs — only show when enough groups exist */}
        {totalGroupCards > 6 && (
          <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
            {CATEGORY_TABS.map((tab) => {
              const isActive = categoryTab === tab.value;
              return (
                <ButtonBase
                  key={tab.value}
                  onClick={() => setCategoryTab(tab.value)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '16px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    minHeight: 32,
                    bgcolor: isActive ? appColors.primary.main : 'transparent',
                    color: isActive ? '#FFFFFF' : appColors.text.body,
                    border: isActive ? 'none' : `1px solid ${appColors.border.default}`,
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: isActive ? appColors.primary.dark : appColors.surface.inactive },
                    '&:focus-visible': { outline: `2px solid ${appColors.primary.main}`, outlineOffset: 2 },
                  }}
                >
                  {tab.label}{categoryCounts[tab.value] > 0 ? ` ${categoryCounts[tab.value]}` : ''}
                </ButtonBase>
              );
            })}
          </Box>
        )}

        {/* Grouped sections */}
        {visibleSections.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: appColors.text.caption }}>
              No group data available.
            </Typography>
          </Box>
        ) : (
          visibleSections.map((section) => (
            <Box key={section.title} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 1 }}>
                {section.title}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 1.5,
                }}
              >
                {section.groups.map((group) => (
                  <RosterGroupCard
                    key={`${group.groupType}:${group.rawValue}`}
                    label={group.label}
                    counts={group.counts}
                    onClick={() => handleGroupCardClick(group)}
                    isClosed={isClosed}
                  />
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* ── Working together banner ── */}
      <WorkingTogetherBanner />

      {/* ── Realtime event snackbar ── */}
      <Snackbar
        open={realtimeSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setRealtimeSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setRealtimeSnackbar((prev) => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {realtimeSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RosterViewSelectionPage;
